import { type ReactNode, useEffect, useState } from "react";
import { Link, useLocation } from "wouter";
import { useSession } from "../lib/session";
import { usePwa } from "../lib/pwa";
import { ChatPanel } from "../agent/ChatPanel";

/**
 * Mobile-first AppShell.
 *
 * Layout responsibilities (in order of priority):
 *
 *   1. Tiny safe-area-respecting topbar (brand + outlet pill + chat toggle).
 *   2. Main content scrolls in the middle.
 *   3. On <md: bottom tab nav (44px tap targets) — Dashboard / Orders /
 *      Suppliers / Invoices / Settings. Chat is a full-screen drawer
 *      triggered by a floating action button.
 *   4. On md+: chat is a docked right-side sidebar (420px), nav stays
 *      in the topbar.
 *
 * The drawer uses `dialog[open]` semantics via state — Escape closes,
 * backdrop tap closes, focus is trapped while open. iOS standalone mode
 * gets safe-area padding so the bottom nav clears the home indicator.
 */
const NAV = [
  { to: "/app/dashboard", label: "Dashboard", short: "Home",      icon: "▦" },
  { to: "/app/orders",    label: "Orders",    short: "Orders",    icon: "▤" },
  { to: "/app/suppliers", label: "Suppliers", short: "Suppliers", icon: "◇" },
  { to: "/app/invoices",  label: "Invoices",  short: "Bills",     icon: "₿" },
  { to: "/app/settings",  label: "Settings",  short: "Settings",  icon: "◉" },
];

export function AppShell({ children }: { children: ReactNode }) {
  const { session, loading, setActiveOutlet } = useSession();
  const [location] = useLocation();
  const [chatOpen, setChatOpen] = useState(false);
  const [outletOpen, setOutletOpen] = useState(false);
  const { canInstall, promptInstall, standalone } = usePwa();

  // Open chat by default on desktop only.
  useEffect(() => {
    if (typeof window === "undefined") return;
    setChatOpen(window.matchMedia("(min-width: 1024px)").matches);
  }, []);

  // Close chat drawer on route change (mobile UX).
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.matchMedia("(max-width: 1023px)").matches) setChatOpen(false);
  }, [location]);

  // Escape closes overlays.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        if (chatOpen) setChatOpen(false);
        else if (outletOpen) setOutletOpen(false);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [chatOpen, outletOpen]);

  if (loading) {
    return (
      <div className="min-h-[100dvh] grid place-items-center text-ink-mute text-sm safe-area-all">
        loading session...
      </div>
    );
  }

  if (!session.loggedIn) {
    if (typeof window !== "undefined") window.location.replace("/login");
    return null;
  }

  const initials =
    (session.user?.firstName?.[0] ?? "?") + (session.user?.lastName?.[0] ?? "");
  const activeOutlet =
    session.outlets?.find((o) => o.outletId === session.activeOutletId) ?? session.outlets?.[0];

  return (
    <div className="min-h-[100dvh] bg-app text-ink flex flex-col">
      {/* ───── topbar ───── */}
      <header
        className="bg-topbar text-white sticky top-0 z-30 safe-area-top"
        style={{ minHeight: "var(--topbar-height)" }}
      >
        <div className="flex items-center px-3 sm:px-6 gap-3 sm:gap-6 h-[var(--topbar-height)]">
          <Link href="/" className="flex items-center gap-2 font-display italic text-lg sm:text-xl shrink-0">
            <span className="size-6 rounded-md bg-nomni" />
            <span className="hidden sm:inline">Nomni</span>
          </Link>

          {/* Desktop nav — hidden on mobile (bottom tabs replace it) */}
          <nav className="hidden md:flex items-center gap-1 text-sm">
            {NAV.map((n) => (
              <Link
                key={n.to}
                href={n.to}
                className={`px-3 py-2 rounded-full transition min-h-11 inline-flex items-center ${
                  location.startsWith(n.to)
                    ? "bg-white/15 text-white"
                    : "text-white/70 hover:text-white"
                }`}
              >
                {n.label}
              </Link>
            ))}
          </nav>

          <div className="ml-auto flex items-center gap-2 relative">
            {activeOutlet ? (
              <button
                onClick={() => setOutletOpen((o) => !o)}
                className="text-xs text-white/85 hover:text-white border border-white/15 rounded-full px-3 min-h-11 max-w-[40vw] sm:max-w-[220px] truncate"
                title={activeOutlet.outletName ?? activeOutlet.name ?? activeOutlet.outletId}
              >
                {activeOutlet.outletName ?? activeOutlet.name ?? activeOutlet.outletId}
                <span className="ml-2 text-white/50">▾</span>
              </button>
            ) : null}
            {outletOpen && session.outlets?.length ? (
              <>
                <button
                  aria-label="Close outlet menu"
                  onClick={() => setOutletOpen(false)}
                  className="fixed inset-0 z-40 bg-black/20 lg:bg-transparent"
                />
                <div className="absolute right-0 top-[calc(var(--topbar-height)+8px)] w-72 max-w-[90vw] rounded-[var(--radius-card)] bg-panel text-ink shadow-[var(--shadow-rise)] ring-1 ring-rule p-1 z-50 max-h-[60vh] overflow-y-auto">
                  {session.outlets.map((o) => (
                    <button
                      key={o.outletId}
                      onClick={() => {
                        setActiveOutlet(o.outletId);
                        setOutletOpen(false);
                      }}
                      className={`w-full text-left px-3 py-3 rounded-lg text-sm hover:bg-mint/50 min-h-11 ${
                        o.outletId === session.activeOutletId ? "bg-mint/60 text-nomni-deep" : ""
                      }`}
                    >
                      <div className="truncate">{o.outletName ?? o.name ?? o.outletId}</div>
                      <div className="text-[10px] text-ink-mute">{o.outletId}</div>
                    </button>
                  ))}
                </div>
              </>
            ) : null}
            {/* Desktop-only chat toggle (mobile uses the FAB) */}
            <button
              onClick={() => setChatOpen((o) => !o)}
              className="hidden lg:inline-flex text-xs text-white/80 hover:text-white border border-white/15 rounded-full px-3 min-h-11 items-center"
            >
              {chatOpen ? "Hide chat" : "Open chat"}
            </button>
            <div className="size-9 rounded-full bg-nomni text-nomni-deep grid place-items-center font-medium text-sm">
              {initials.toUpperCase()}
            </div>
          </div>
        </div>
      </header>

      {/* ───── main + (desktop) docked chat ───── */}
      <div className={`flex-1 grid ${chatOpen ? "lg:grid-cols-[1fr_420px]" : "grid-cols-1"} min-h-0`}>
        <main className="min-h-0 overflow-y-auto pb-[calc(var(--mobile-tab-height)+env(safe-area-inset-bottom))] md:pb-0">
          {!standalone && canInstall ? (
            <div className="px-4 pt-4 md:px-8">
              <button
                onClick={() => promptInstall()}
                className="w-full md:w-auto rounded-full bg-nomni-deep text-white px-5 py-2.5 text-sm font-medium hover:bg-nomni-leaf"
              >
                Install Nomni · open like a native app
              </button>
            </div>
          ) : null}
          {children}
        </main>

        {/* Desktop docked chat */}
        {chatOpen ? (
          <aside className="hidden lg:block border-l border-rule bg-cream/40 min-h-0">
            <ChatPanel outletId={session.activeOutletId} market={session.market} />
          </aside>
        ) : null}
      </div>

      {/* ───── mobile bottom tab nav (component MobileTabBar) ───── */}
      <MobileTabBar
        items={NAV}
        location={location}
        onOpenChat={() => setChatOpen(true)}
      />

      {/* ───── mobile chat drawer ───── */}
      {chatOpen ? (
        <div className="lg:hidden fixed inset-0 z-50 bg-app safe-area-top safe-area-bottom flex flex-col">
          <div className="flex items-center justify-between px-4 py-2 border-b border-rule">
            <div className="text-sm font-medium">Chat with Nomni</div>
            <button
              onClick={() => setChatOpen(false)}
              className="rounded-full px-4 min-h-11 text-sm text-ink-2 hover:bg-rule/60"
            >
              Close
            </button>
          </div>
          <div className="flex-1 min-h-0">
            <ChatPanel outletId={session.activeOutletId} market={session.market} />
          </div>
        </div>
      ) : null}
    </div>
  );
}

// --- MobileTabBar -----------------------------------------------------------
// 5 primary nav items + a center "chat" FAB. Native-feeling bottom nav with
// safe-area padding for the home indicator.
function MobileTabBar({
  items,
  location,
  onOpenChat,
}: {
  items: typeof NAV;
  location: string;
  onOpenChat: () => void;
}) {
  return (
    <nav
      className="md:hidden fixed bottom-0 inset-x-0 z-30 bg-panel/95 backdrop-blur ring-1 ring-rule safe-area-bottom"
      style={{ height: "calc(var(--mobile-tab-height) + env(safe-area-inset-bottom))" }}
      aria-label="Primary"
    >
      <div className="grid grid-cols-6 h-[var(--mobile-tab-height)]">
        {items.slice(0, 2).map((n) => (
          <NavTab key={n.to} {...n} active={location.startsWith(n.to)} />
        ))}
        <button
          onClick={onOpenChat}
          aria-label="Open chat"
          className="flex flex-col items-center justify-center min-h-11 text-nomni-deep"
        >
          <span className="size-10 rounded-full bg-nomni-deep text-white grid place-items-center -mt-3 shadow-[var(--shadow-rise)]">
            <span className="text-base">✦</span>
          </span>
          <span className="text-[10px] mt-0.5">Chat</span>
        </button>
        {items.slice(2, 5).map((n) => (
          <NavTab key={n.to} {...n} active={location.startsWith(n.to)} />
        ))}
      </div>
    </nav>
  );
}

function NavTab({ to, short, icon, active }: { to: string; short: string; icon: string; active: boolean }) {
  return (
    <Link
      href={to}
      className={`flex flex-col items-center justify-center min-h-11 ${
        active ? "text-nomni-deep" : "text-ink-mute"
      }`}
    >
      <span className={`text-lg leading-none ${active ? "text-nomni-deep" : ""}`}>{icon}</span>
      <span className="text-[10px] mt-0.5">{short}</span>
    </Link>
  );
}
